import FormInput from "../../molecules/FormInput";
import { useEffect, useState } from "react";
import { Stack } from "@mui/system";
import { Department, TicketPriorityEnum } from "../../../services/swagger";
import { useGlobalContext } from "../../../stores/Global";
import AvatarBanner from "../../molecules/AvatarBanner";
import {
  CompanyService,
  DepartmentService,
  SettingsService,
} from "../../../services/api";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import useEnums from "../../../hooks/useEnums";
import { DropDownItem } from "../../molecules/Dropdown";
import Authorized from "../../atoms/Authorized";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import AvatarWithInitials from "../../atoms/AvatarWithInitials";
import { Business } from "@mui/icons-material";
import { useBusinessStore } from "../../../stores/Dummy";

export interface CreateTicketInfoFields {
  company: string;
  city: string;
  voucher: string;
  priority: TicketPriorityEnum;
  departmentId: string;
}

interface CreateTicketInfoProps {
  onChange: (fields: CreateTicketInfoFields) => void;
}

export default function CreateTicketInfo(props: CreateTicketInfoProps) {
  const { t } = useTranslation("Ticket");
  const { priorityDropDownItems } = useEnums();
  const { context } = useGlobalContext();
  const [company, setCompany] = useState("");
  const [voucher, setVoucher] = useState("");
  const [cities, setCities] = useState<DropDownItem[]>([]);
  const [city, setCity] = useState("");
  const [priority, setPriority] = useState<TicketPriorityEnum>("medium");
  const [departmentId, setDepartmentId] = useState("");
  const [departments, setDepartments] = useState<Department[]>([]);
  const [voucherAdd, setVoucherAdd] = useState(false);
  const miniCompanyList = useBusinessStore((state) => state.miniCompanyList);
  const hideUI = false;

  const { mutate: loadLists, isPending } = useMutation({
    mutationFn: async () => {
      const [depRes, cityRes] = await Promise.all([
        DepartmentService.getDepartmentMiniList(),
        SettingsService.getCities(),
      ]);

      return {
        cities: cityRes?.data?.data || [],
        departments: depRes?.data?.data || [],
      };
    },
    onSuccess: (data) => {
      setCities(data.cities.map((e) => ({ name: e, value: e })));
      setDepartments(data.departments);

      if (data.departments.length > 0)
        setDepartmentId(data.departments[0]._id || "");
      if (data.cities.length > 0) setCity(data.cities[0]);
    },
  });

  useEffect(() => {
    props.onChange({
      company,
      city,
      voucher,
      priority,
      departmentId,
    });
  }, [company, voucher, priority, city, departmentId]);

  useEffect(() => {
    loadLists();
  }, []);

  return (
    <Paper sx={{ p: 2, bgcolor: "background.default" }}>
      <Stack spacing={2}>
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <AvatarWithInitials size={40}>
            {context.user?.name || ""}
          </AvatarWithInitials>
          <Stack>
            <Typography fontSize="medium" fontWeight={600}>
              {context.user?.name}
            </Typography>
            <Typography color="text.secondary">
              {context.user?.companyName}
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Stack spacing={2}>
          <Authorized role="agent">
            <FormInput
              role="agent"
              field="company"
              label={t("Company")}
              placeholder={t("CompanyPlaceholder")}
              onChange={(v) => {
                setCompany(v as string);
              }}
              onSoloTextChange={(v) => console.log(v)}
              type="list"
              list={miniCompanyList.map?.((company) => ({
                name: `${company.name} (${company.regNo})`,
                value: company._id,
              }))}
              value={company}
              loading={isPending}
              required
            />
          </Authorized>
          <FormInput
            field="priority"
            label={t("Priority")}
            onChange={(v) => setPriority(v as TicketPriorityEnum)}
            type="list"
            value={priority}
            list={priorityDropDownItems}
            required
            loading={isPending}
          />
          <FormInput
            field="city"
            label={t("City")}
            type="list"
            placeholder={t("CityPlaceholder")}
            onChange={(v) => setCity(v as string)}
            value={city}
            list={cities}
            required
            loading={isPending}
          />
          <FormInput
            field="departmentId"
            label={t("Department")}
            type="list"
            placeholder={t("DepartmentPlaceholder")}
            list={departments.map((department) => {
              return {
                name: department.name,
                value: department._id || "",
              };
            })}
            onChange={(v) => setDepartmentId(v as string)}
            value={departmentId}
            required
            loading={isPending}
          />
          {hideUI &&
            (voucherAdd ? (
              <FormInput
                field="voucher"
                label={t("Voucher")}
                onChange={(v) => setVoucher(v as TicketPriorityEnum)}
                type="string"
                value={voucher}
                placeholder="XXXX-XXXX-XXXX-XXXX"
              />
            ) : (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setVoucherAdd(true)}
              >
                {t("AddVoucher")}
              </Button>
            ))}
        </Stack>

        <Button
          variant="contained"
          color="success"
          type="submit"
          loading={isPending}
        >
          {t("CreateTicket")}
        </Button>
      </Stack>
    </Paper>
  );
}
